import {BUDGET_TYPE, PERIOD_BUDGET_TYPE, PROGRAM_BUDGET_TYPE} from '../../../../../../../constants';
import {TRACKING_OBJECTS} from '../../../../../../../tracking/amplitude/constants/TrackingObjects';
import {trackEvent} from '../../../../../../../tracking/amplitude/TrackingV2';
import {notNullishAndPredicate} from '../../../../../util/NotNullPredicate';
import {canLoggedInPersonManageProgram, hasTopDownProgramBudgetFeature} from '../../../../../util/ProgramFinancialLogic';
import {isTimeRegistrationNoteInvalid} from '../../../../../util/time-registration/time-registration-settings/TimeRegistrationNoteFilter';
import {getTaskTimeRegistrationNotAllowedWithReason} from '../../../../../util/time-registration/time-registration-settings/TimeRegistrationTaskFilter';
import {InvalidatedTimerTask} from '../../../../add-time-entry/TimerTaskInvalidInfo';
import {MODAL_TYPE, showModal} from '../../../generic_modal_conductor';
import {ACTION} from '../../../program-management/ProgramBudgetErrorMessage';
import {TimeRegistrationEntity} from '../types/TimeRegistrationEntityId';
import type {TimeRegistrationModal_entity, TimeRegistrationModal_entityProject} from '../types/TimeRegistrationModalEntity';
import {getEntityProject, isTargetEntityBillable} from '../util/TimeRegistrationModalUtil';
import {getBillableMinutesForMutation} from '../../../../../util/time-registration/time-registration-settings/BillableTimeSplitUtil';
import {isProjectDone} from '../../../../../util/projectStageUtil';

type ShowProgramRevenueLockedMessageInput = {
	programName: string | null;
	programPrefix: string | null;
	programMembers: unknown | null; // Could define from viewer
	companyProjectId: number | null;
	actualPersonId: string | null;
};

function showProgramRevenueLockedMessage({
	programName,
	programPrefix,
	programMembers,
	companyProjectId,
	actualPersonId,
}: ShowProgramRevenueLockedMessageInput): void {
	const canManageProgram = canLoggedInPersonManageProgram(programMembers, actualPersonId);

	showModal({
		type: MODAL_TYPE.PROGRAM_BUDGET_ERROR_MESSAGE,
		action: ACTION.ADD_TIME_ENTRY,
		programName,
		canManageProgram,
		programPrefix,
		companyProjectId,
		actualPersonId,
	});

	trackEvent('Program Budget Error Message Modal', 'Shown');
}

type EntityInProgramOverRevenueCapInput = {
	targetEntity: TimeRegistrationModal_entity;
	actualPersonId: string | null;
	time: number | null;
	billableTime: number | null;
};

function isExemptFromRevenueCap(entityProject: NonNullable<TimeRegistrationModal_entityProject>): boolean {
	return (
		entityProject.budgetType === BUDGET_TYPE.FIXED_PRICE ||
		entityProject.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 ||
		(entityProject.budgetType === BUDGET_TYPE.RETAINER &&
			entityProject.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE)
	);
}

function entityInProgramOverRevenueCap({
	targetEntity,
	actualPersonId,
	time,
	billableTime,
}: EntityInProgramOverRevenueCapInput): boolean {
	const hasTopDownProgramBudgetFeatureFlag = hasTopDownProgramBudgetFeature();
	const entityProject = getEntityProject(targetEntity);
	const billableMinutesRegistered = getBillableMinutesForMutation(isTargetEntityBillable(targetEntity), billableTime);
	const registrationIsNonBillable =
		billableMinutesRegistered === undefined ||
		billableMinutesRegistered === 0 ||
		(billableMinutesRegistered === null && time === 0);
	if (entityProject && entityProject.program && hasTopDownProgramBudgetFeatureFlag) {
		const projectIsInOverRevenueCapProgram =
			entityProject.isProgramRevenueLocked &&
			!registrationIsNonBillable &&
			entityProject.programBudgetType === PROGRAM_BUDGET_TYPE.CAPPED &&
			!isExemptFromRevenueCap(entityProject);
		if (projectIsInOverRevenueCapProgram) {
			showProgramRevenueLockedMessage({
				programName: entityProject.program.name,
				programPrefix: entityProject.program.prefix,
				programMembers: entityProject.program.members,
				companyProjectId: entityProject.companyProjectId,
				actualPersonId,
			});
			return true;
		}
	}
	return false;
}

export enum ModalInvalidInputReason {
	InvalidNote = 'InvalidNote',
	InvalidTime = 'InvalidTime',
	InvalidEntity = 'InvalidEntity',
}

type IsModalInputInvalid = {
	targetEntity: TimeRegistrationModal_entity;
	actualPersonId: string | null;
	time: number | null;
	billableTime: number | null;
	note: string;
};

export function isModalInputInvalid({
	targetEntity,
	actualPersonId,
	time,
	billableTime,
	note,
}: IsModalInputInvalid): ModalInvalidInputReason[] {
	const invalidReasons: ModalInvalidInputReason[] = [];
	if (time === null || isNaN(time)) {
		trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Submit Failed', {reason: 'Time Invalid'});
		invalidReasons.push(ModalInvalidInputReason.InvalidTime);
	}

	if (isTimeRegistrationNoteInvalid(note)) {
		trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Submit Failed', {reason: 'Note Invalid'});
		invalidReasons.push(ModalInvalidInputReason.InvalidNote);
	}

	if (entityInProgramOverRevenueCap({targetEntity, actualPersonId, time, billableTime})) {
		trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Submit Failed', {
			reason: 'Program Revenue Cap Exceeded',
		});
		invalidReasons.push(ModalInvalidInputReason.InvalidEntity);
	}

	return invalidReasons;
}

type ValidateTargetEntityInput = {
	targetEntity: TimeRegistrationModal_entity | null;
	personId: string | null;
	shouldValidateEntity: boolean;
	showError: boolean;
};
type ValidateTargetEntityOutputNoError = {
	targetEntity: TimeRegistrationModal_entity | null;
	targetEntityError: null;
};
type ValidateTargetEntityOutputError = {targetEntity: null; targetEntityError: InvalidatedTimerTask};

export function validateTargetEntity({
	targetEntity,
	personId,
	shouldValidateEntity,
	showError,
}: ValidateTargetEntityInput): ValidateTargetEntityOutputNoError | ValidateTargetEntityOutputError {
	if (personId && shouldValidateEntity) {
		if (targetEntity?.entity === TimeRegistrationEntity.Task) {
			const statusCategory = targetEntity.statusColumnV2 ? targetEntity.statusColumnV2.category : null;
			const assignedPersonIds = targetEntity.assignedPersons
				? targetEntity.assignedPersons.filter(notNullishAndPredicate()).map(person => person.id)
				: [];

			const timeRegistrationNotAllowedReason = getTaskTimeRegistrationNotAllowedWithReason(
				statusCategory,
				personId,
				assignedPersonIds
			);
			if (timeRegistrationNotAllowedReason) {
				return {
					targetEntity: null,
					targetEntityError: showError
						? {taskId: targetEntity.companyTaskId, reason: timeRegistrationNotAllowedReason}
						: null,
				};
			}
			if (!targetEntity?.project?.fullAccessToProject || isProjectDone(targetEntity.project)) {
				// No access to task's project or project DONE
				return {targetEntity: null, targetEntityError: null};
			}
		} else if (targetEntity?.entity === TimeRegistrationEntity.Project) {
			if (!targetEntity.fullAccessToProject || isProjectDone(targetEntity)) {
				// No access to project or project DONE
				return {targetEntity: null, targetEntityError: null};
			}
		}
	}

	return {targetEntity, targetEntityError: null};
}
